"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button, Badge, Label } from "@eg/ui";
import ImageWithFallback from "../ImageWithFallback";
import { useCartStore } from "@/store/cart";
import type { EnrichedProduct } from "@/lib/discovery";
import { formatPrice } from "@/lib/format";
import { useLocale, useTranslations } from "@/lib/i18n/I18nProvider";

type Props = {
  product: EnrichedProduct | null;
  onClose: () => void;
};

export function QuickViewModal({ product, onClose }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const [imageIndex, setImageIndex] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    null,
  );
  const addItem = useCartStore((s) => s.addItem);
  const t = useTranslations();
  const locale = useLocale();

  useEffect(() => {
    if (!product) return;
    setImageIndex(0);
    setSelectedVariantId(
      product.variants.find((v) => v.stock > 0)?.id ?? product.variants[0]?.id,
    );
  }, [product]);

  useEffect(() => {
    if (!product) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    closeBtnRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
      if (event.key === "Tab" && dialogRef.current) {
        const focusables = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => !el.hasAttribute("disabled"));

        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault();
            last.focus();
          }
        } else if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus();
    };
  }, [onClose, product]);

  useEffect(() => {
    if (!product) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [product]);

  const selectedVariant = useMemo(() => {
    if (!product) return null;
    return product.variants.find((v) => v.id === selectedVariantId);
  }, [product, selectedVariantId]);

  if (!product) return null;

  const normalizedImages = (product.images ?? []).filter(
    (img): img is string => typeof img === "string" && img.trim().length > 0,
  );
  const images =
    normalizedImages.length > 0
      ? normalizedImages
      : typeof product.heroImage === "string" && product.heroImage.trim().length
        ? [product.heroImage]
        : [];
  const safeImageIndex = Math.min(imageIndex, Math.max(images.length - 1, 0));
  const activeImage = images[safeImageIndex] ?? "";
  const hasMultiple = images.length > 1;

  const priceText =
    selectedVariant &&
    formatPrice(selectedVariant.priceCents, selectedVariant.currency, locale);

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="h-full w-full overflow-y-auto md:flex md:items-center md:justify-center md:px-4 md:py-6">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          className="relative w-full bg-white shadow-2xl md:max-w-5xl md:overflow-hidden md:rounded-3xl md:h-[min(90vh,780px)]"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            ref={closeBtnRef}
            aria-label={t("common.close")}
            className="absolute right-3 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 shadow-sm transition hover:rotate-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 md:right-4"
            style={{ top: "max(0.75rem, env(safe-area-inset-top))" }}
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="grid min-h-[100dvh] grid-cols-1 md:h-full md:min-h-0 md:grid-cols-2">
            <div className="relative bg-gray-50">
              <div className="aspect-square w-full max-w-full">
                <ImageWithFallback
                  src={activeImage}
                  alt={product.title}
                  className="h-full w-full object-contain"
                />
              </div>

              {hasMultiple ? (
                <>
                  <button
                    className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-slate-300 bg-white/95 p-2 shadow-sm transition hover:-translate-y-1/2 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2"
                    onClick={() =>
                      setImageIndex((prev) =>
                        prev === 0 ? images.length - 1 : prev - 1,
                      )
                    }
                    aria-label={t("discovery.quickView.prevImage")}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-slate-300 bg-white/95 p-2 shadow-sm transition hover:-translate-y-1/2 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2"
                    onClick={() =>
                      setImageIndex((prev) => (prev + 1) % images.length)
                    }
                    aria-label={t("discovery.quickView.nextImage")}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              ) : null}

              {hasMultiple ? (
                <div className="flex gap-2 overflow-auto px-4 py-3">
                  {images.map((img, idx) => (
                    <button
                      key={`${img}-${idx}`}
                      className={`h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 ${
                        idx === safeImageIndex
                          ? "border-cyan-500"
                          : "border-slate-300"
                      }`}
                      onClick={() => setImageIndex(idx)}
                    >
                      <ImageWithFallback
                        src={img}
                        alt={`${product.title} ${idx + 1}`}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="flex min-h-0 flex-col gap-4 px-6 pb-6 pt-4 md:p-8 md:pr-14 md:pt-12">
              <div className="min-h-0 space-y-4 md:overflow-y-auto md:pr-1">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-gray-500">
                      {product.brand}
                    </p>
                    <h2 className="text-2xl font-semibold text-gray-900">
                      {product.title}
                    </h2>
                  </div>
                  <Badge
                    variant="outline"
                    className="rounded-full border-cyan-300 bg-cyan-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-900"
                  >
                    {t("discovery.quickView.badge")}
                  </Badge>
                </div>

                {product.description ? (
                  <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">
                    {product.description}
                  </p>
                ) : null}

                <div className="grid gap-3">
                  <Label className="text-sm font-semibold text-gray-800">
                    {t("discovery.quickView.chooseVariant")}
                  </Label>
                  <div className="grid gap-2">
                    {product.variants.map((variant) => {
                      const isSelected = variant.id === selectedVariantId;
                      const priceLabel = formatPrice(
                        variant.priceCents,
                        variant.currency,
                        locale,
                      );
                      return (
                        <button
                          key={variant.id}
                          onClick={() => setSelectedVariantId(variant.id)}
                          className={`flex w-full items-center justify-between rounded-xl border px-3 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 ${
                            isSelected
                              ? "border-cyan-400 bg-cyan-50 text-slate-900 shadow-sm"
                              : "border-slate-300 bg-white hover:border-cyan-300"
                          }`}
                        >
                          <div>
                            <p
                              className={`text-sm font-semibold ${
                                isSelected ? "text-slate-900" : "text-slate-900"
                              }`}
                            >
                              {variant.title}
                            </p>
                            <p
                              className={`text-xs ${
                                isSelected ? "text-slate-700" : "text-slate-600"
                              }`}
                            >
                              {variant.stock > 0
                                ? t("product.stock.inStock", {
                                    count: variant.stock,
                                  })
                                : t("discovery.quickView.notAvailable")}
                            </p>
                          </div>
                          <span
                            className={`text-sm font-semibold ${
                              isSelected ? "text-slate-900" : "text-slate-900"
                            }`}
                          >
                            {priceLabel}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {selectedVariant ? (
                <div className="flex items-center gap-3 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 shadow-inner">
                  <div className="text-lg font-semibold text-emerald-900">
                    {priceText}
                  </div>
                  <div className="text-xs font-medium text-emerald-800">
                    {selectedVariant.stock > 0
                      ? t("discovery.quickView.availableNow")
                      : t("discovery.quickView.currentlyNotAvailable")}
                  </div>
                </div>
              ) : null}

              <div className="mt-auto flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  variant="accent"
                  className="flex-1"
                  disabled={!selectedVariant || selectedVariant.stock <= 0}
                  onClick={() => {
                    if (!selectedVariant) return;
                    addItem(
                      {
                        variantId: selectedVariant.id,
                        variantSlug: selectedVariant.slug,
                        productTitle: product.title,
                        variantTitle: selectedVariant.title,
                        priceCents: selectedVariant.priceCents,
                        currency: selectedVariant.currency,
                        image: product.heroImage,
                      },
                      1,
                    );
                    onClose();
                  }}
                >
                  {t("discovery.product.addToCart")}
                </Button>
                <Button variant="ghost" onClick={onClose}>
                  {t("common.close")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
